/**
 * THE STANDALONE ENTRY POINT into Buyer Activity.
 *
 * Buyer Activity used to be reachable only from inside an existing
 * conversation, which meant the most actionable person on the lot — someone who
 * just tapped a card and is standing there browsing — was invisible until they
 * sent a message. This hangs off the tap instead: one row per buyer SESSION,
 * live or from earlier today, each opening its own feed.
 *
 * Identity is `session_id`, always. There is no "current buyer" singleton, so
 * two shoppers on two cars are two rows and never one ambiguous view.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Car, Loader2, X } from 'lucide-react';

import { buyerActivityAPI } from '../../lib/api';
import BuyerActivityPanel, { actionLabelFor } from './BuyerActivityPanel';
import LiveNowBadge from './LiveNow';

const POLL_MS = 10000;

const rel = (iso, t) => {
    if (!iso) return '';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return t('buyerActivity.justNow', 'just now');
    if (mins < 60) return t('buyerActivity.minsAgo', '{{n}} mins ago', { n: mins });
    const hrs = Math.floor(mins / 60);
    return t('buyerActivity.hrsAgo', '{{n}} h ago', { n: hrs });
};

const BuyerRow = ({ b, onOpen, t }) => (
    <button type="button" onClick={() => onOpen(b)}
        data-testid={`live-buyer-${b.session_id}`}
        data-live={b.live ? '1' : '0'}
        className={`w-full flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition-colors ${
            b.live
                ? 'border-rose-500/40 bg-rose-500/[0.07] hover:bg-rose-500/[0.12]'
                : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07]'}`}>
        {b.photo ? (
            <img src={b.photo} alt="" loading="lazy"
                className="w-14 h-11 rounded-lg object-cover bg-slate-800 shrink-0" />
        ) : (
            <span className="w-14 h-11 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                <Car className="w-4 h-4 text-slate-500" />
            </span>
        )}
        <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2">
                <span className="text-[14px] font-semibold text-white truncate">
                    {b.label || b.card_id}
                </span>
                {b.live && <LiveNowBadge testid={`live-buyer-badge-${b.session_id}`} />}
            </span>
            <span className="block text-[12px] text-slate-400 truncate">
                {b.customer_name || t('liveBuyers.someone', 'Someone')}
                {b.stock_number ? ` · #${b.stock_number}` : ''}
                {' · '}
                {t('liveBuyers.actions', { count: Number(b.event_count || 0) })}
            </span>
            <span className="block text-[12px] text-slate-500 truncate">
                {actionLabelFor(b.last_event_type, t, b.last_request_type)} · {rel(b.last_at, t)}
            </span>
        </span>
    </button>
);

export default function LiveBuyersButton({ organizationId,
                                          autoGoLive = false, compact = false,
                                          // `inline` renders the live rows straight into the desk's
                                          // notification area instead of a header button: the tap
                                          // notification itself becomes the thing you click.
                                          inline = false }) {
    const { t } = useTranslation();
    const [buyers, setBuyers] = useState(null);
    const [listOpen, setListOpen] = useState(false);
    const [picked, setPicked] = useState(null);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const r = await buyerActivityAPI.liveBuyers(organizationId);
            setBuyers(r.data?.buyers || []);
        } catch {
            setBuyers([]);
        }
    }, [organizationId]);

    useEffect(() => {
        load();
        const iv = setInterval(load, POLL_MS);
        return () => clearInterval(iv);
    }, [load]);

    const live = (buyers || []).filter((b) => b.live);
    const earlier = (buyers || []).filter((b) => !b.live);

    // Exactly one live buyer goes straight in; two or more always show the list,
    // because guessing which shopper the salesperson meant is the one thing
    // this must never do.
    const openEntry = () => {
        if (live.length === 1) setPicked(live[0]);
        else setListOpen(true);
    };

    // No auto-open, in any context. Live activity announces itself with the
    // count badge on the entry point; the panel is only ever opened by a tap,
    // so it can never sit on top of the controls underneath it.

    return (
        <>
            {inline ? (
                live.length > 0 && (
                    <section data-testid="live-buyers-inline" className="space-y-2 mb-4">
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-300/90">
                            {t('liveBuyers.liveNow', 'On the lot right now')}
                        </p>
                        {live.map((b) => <BuyerRow key={b.session_id} b={b} onOpen={setPicked} t={t} />)}
                        {earlier.length > 0 && (
                            <button type="button" onClick={() => setListOpen(true)}
                                data-testid="live-buyers-see-all"
                                className="text-[12px] font-semibold text-slate-400 hover:text-slate-200 transition-colors">
                                {t('liveBuyers.seeAll', 'See everyone who tapped today ({{n}})', { n: (buyers || []).length })}
                            </button>
                        )}
                    </section>
                )
            ) : (
                <button type="button" onClick={openEntry} data-testid="live-buyers-btn"
                    aria-label={t('liveBuyers.title', 'Buyer Activity')}
                    className={`relative inline-flex items-center gap-1.5 text-xs font-semibold rounded-full transition-colors ${
                        live.length
                            ? 'text-white bg-rose-600 hover:bg-rose-500'
                            : 'text-slate-300 border border-white/15 hover:text-white'
                    } ${compact ? 'w-8 h-8 justify-center px-0' : 'px-3 py-1.5'}`}>
                    <Activity className="w-4 h-4" />
                    {!compact && (live.length
                        ? t('liveBuyers.liveNowCount', '{{n}} live now', { n: live.length })
                        : t('liveBuyers.title', 'Buyer Activity'))}
                    {compact && live.length > 0 && (
                        <span data-testid="live-buyers-count"
                            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500
                                       text-white text-[10px] font-bold flex items-center justify-center">
                            {live.length}
                        </span>
                    )}
                </button>
            )}

            {listOpen && (
                <div className="fixed inset-0 z-[70] flex justify-end pointer-events-none pt-[200px] md:pt-16"
                    data-testid="live-buyers-panel">
                    <aside className="pointer-events-auto h-full w-[88%] md:w-full md:max-w-md bg-slate-950 border-l border-white/10
                                      overflow-y-auto overscroll-contain shadow-2xl shadow-black/60
                                      animate-in slide-in-from-right duration-200">
                        <header className="sticky top-0 z-10 flex items-center gap-2.5 px-4 py-3.5
                                           bg-slate-950/95 backdrop-blur border-b border-white/10">
                            <Activity className="w-4 h-4 text-emerald-400 shrink-0" />
                            <h2 className="text-base font-bold text-white flex-1">
                                {t('liveBuyers.title', 'Buyer Activity')}
                            </h2>
                            <button type="button" onClick={() => setListOpen(false)}
                                data-testid="live-buyers-close" aria-label={t('common.close', 'Close')}
                                className="-mr-2 w-11 h-11 flex items-center justify-center text-slate-400
                                           hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </header>

                        <div className="px-4 py-4 space-y-5">
                            <p className="text-[12px] text-slate-400 leading-relaxed">
                                {t('liveBuyers.hint', 'Anyone who tapped a card here — no message needed. Open one to follow what they are doing, or read back what they already did.')}
                            </p>

                            {buyers === null ? (
                                <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin" />{t('salesDesk.loading', 'Loading…')}
                                </div>
                            ) : buyers.length === 0 ? (
                                <p className="text-sm text-slate-400 py-8 text-center" data-testid="live-buyers-empty">
                                    {t('liveBuyers.empty', 'Nobody has tapped a card in the last 24 hours.')}
                                </p>
                            ) : (
                                <>
                                    {live.length > 0 && (
                                        <section data-testid="live-buyers-live-section" className="space-y-2">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-300/90">
                                                {t('liveBuyers.liveNow', 'On the lot right now')}
                                            </p>
                                            {live.map((b) => <BuyerRow key={b.session_id} b={b} onOpen={setPicked} t={t} />)}
                                        </section>
                                    )}
                                    {earlier.length > 0 && (
                                        <section data-testid="live-buyers-earlier-section" className="space-y-2">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                                                {t('liveBuyers.earlier', 'Earlier (last 24 hours)')}
                                            </p>
                                            {earlier.map((b) => <BuyerRow key={b.session_id} b={b} onOpen={setPicked} t={t} />)}
                                        </section>
                                    )}
                                </>
                            )}
                        </div>
                    </aside>
                </div>
            )}

            {picked && (
                <BuyerActivityPanel
                    open
                    sessionId={picked.session_id}
                    organizationId={organizationId}
                    title={picked.label || picked.card_id}
                    autoGoLive={autoGoLive || picked.live}
                    onClose={() => setPicked(null)} />
            )}
        </>
    );
}
